"use client";

import { Button } from "@shared/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import type * as React from "react";
import { useEffect } from "react";
import { buildLoginUrl, getCurrentReturnToPath, useAuth } from "@/lib/auth";
import { isTabVisible } from "@/lib/auth/session-recovery";

type AuthGateProps = {
  children: React.ReactNode;
};

export const AuthGate = ({ children }: AuthGateProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { user, loading, status, meErrorSummary } = useAuth();

  useEffect(() => {
    if (loading || user || status !== "unauthenticated") {
      return;
    }

    const redirectToLogin = () => {
      router.replace(buildLoginUrl(getCurrentReturnToPath()));
    };

    if (isTabVisible()) {
      redirectToLogin();
      return;
    }

    // Session is dead but the tab is in the background — wait until the user
    // returns before navigating away (avoids surprising background redirects).
    const onVisible = () => {
      if (!isTabVisible()) {
        return;
      }
      redirectToLogin();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [loading, user, status, router]);

  // Only block on loading when no user has been resolved yet (initial load).
  // During background refetches loading is true but user is already populated —
  // gating on loading in that case causes a visible flash for authenticated users.
  //
  // When status === "error": pages are not blocked here so that page-level
  // error recovery UI (e.g. the billing page retry panel) can render.
  if ((!user && loading) || status === "unauthenticated") {
    return null;
  }

  // me query failed without a user — don't render the app shell in a half-broken
  // "Sign in" + perpetual Loading state (e.g. GraphQL schema mismatch).
  if (!user && status === "error") {
    return (
      <div className="flex min-h-svh flex-1 flex-col items-center justify-center gap-4 p-6">
        <div className="w-full max-w-md space-y-4 text-center">
          <h2 className="text-lg font-semibold tracking-tight">We could not load your account</h2>
          <p className="text-sm text-muted-foreground">Refresh the page and try again, or contact support.</p>
          {meErrorSummary ? (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-left font-mono text-xs text-muted-foreground wrap-break-word">
              {meErrorSummary}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                void queryClient.invalidateQueries({ queryKey: ["me"] });
              }}
            >
              Retry
            </Button>
            <Button type="button" variant="outline" asChild>
              <a href="mailto:support@radium.cloud">Contact support</a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};
